/**
 * Offensive display-name detection (shared by client and Cloud Functions).
 *
 * Two tiers:
 * - **Definite** (`checkNameDefinite`): user pick, updateUserName, sign-up, safety-net.
 *   Blocks when a slur matches an entire token or compact string; hate/custom patterns
 *   also match as substrings within a token (profanity does not — avoids Dickson FPs).
 * - **Strict** (`checkNameStrict`): admin dry-run scan and enforce only.
 *   Substring matching on raw, normalized, and compact forms.
 *
 * Uses obscenity English preset + hate-speech extensions and a normalization pipeline.
 */
import {
  DataSet,
  RegExpMatcher,
  englishRecommendedTransformers,
  type MatchPayload,
} from 'obscenity';
import { isAllowlistedNormalizedName } from './nameModerationAllowlist';
import { applyHateSpeechExtensions, categoryForOriginalWord } from './nameModerationExtensions';

export const MODERATION_RESET_NAME = 'Name';

export type NameMatchCategory = 'profanity' | 'hate' | 'custom';

export interface NameCheckResult {
  offensive: boolean;
  category?: NameMatchCategory;
  /** Internal pattern id — never log or expose the matched slur */
  matchedPatternId?: string;
}

const ZERO_WIDTH_RE = /[\u200B-\u200D\uFEFF]/g;
const COMBINING_MARKS_RE = /[\u0300-\u036f]/g;

/** Cyrillic / Greek lookalikes → Latin */
const HOMOGLYPH_MAP: Record<string, string> = {
  а: 'a',
  е: 'e',
  о: 'o',
  р: 'p',
  с: 'c',
  у: 'y',
  х: 'x',
  і: 'i',
  Α: 'a',
  Β: 'b',
  Ε: 'e',
  Η: 'h',
  Ι: 'i',
  Κ: 'k',
  Μ: 'm',
  Ν: 'n',
  Ο: 'o',
  Ρ: 'p',
  Τ: 't',
  Χ: 'x',
};

const LEET_REPLACEMENTS: [RegExp, string][] = [
  [/[@]/g, 'a'],
  [/4/g, 'a'],
  [/8/g, 'b'],
  [/[(]/g, 'c'],
  [/3/g, 'e'],
  [/6/g, 'g'],
  [/9/g, 'g'],
  [/1|!|\|/g, 'i'],
  [/0/g, 'o'],
  [/5|\$/g, 's'],
  [/7/g, 't'],
  [/\+/g, 't'],
  [/2/g, 'z'],
];

type DatasetMeta = { originalWord: string; category?: 'hate' | 'custom' };

const moderationDataset = applyHateSpeechExtensions(
  new DataSet<DatasetMeta>(),
);

const matcher = new RegExpMatcher({
  ...moderationDataset.build(),
  ...englishRecommendedTransformers,
});

function foldHomoglyphs(input: string): string {
  return [...input].map((ch) => HOMOGLYPH_MAP[ch] ?? ch).join('');
}

function applyLeetSpeak(input: string): string {
  let out = input;
  for (const [re, replacement] of LEET_REPLACEMENTS) {
    out = out.replace(re, replacement);
  }
  return out;
}

/** Normalize a display name for moderation matching. */
export function normalizeForModeration(input: string): string {
  let s = input.normalize('NFKC').replace(ZERO_WIDTH_RE, '');
  s = foldHomoglyphs(s);
  s = s.replace(COMBINING_MARKS_RE, '');
  s = applyLeetSpeak(s);
  s = s.replace(/[^\p{L}\p{N}\s]/gu, '');
  s = s.replace(/(.)\1{2,}/g, '$1$1');
  s = s.replace(/\s+/g, ' ').trim().toLowerCase();
  return s;
}

/** Strip spaces for embedded-token detection (e.g. "f u c k"). */
export function normalizeCompact(input: string): string {
  return normalizeForModeration(input).replace(/\s+/g, '');
}

function matchOnCandidate(text: string): MatchPayload | null {
  if (!text) return null;
  const matches = matcher.getAllMatches(text, true);
  return matches.length > 0 ? matches[0] : null;
}

function matchCoversEntireCandidate(text: string, match: MatchPayload): boolean {
  return match.startIndex === 0 && match.matchLength === text.length;
}

function resultFromMatch(match: MatchPayload): NameCheckResult {
  const { phraseMetadata } = moderationDataset.getPayloadWithPhraseMetadata(match);
  const originalWord = phraseMetadata?.originalWord ?? 'unknown';
  const category =
    phraseMetadata?.category === 'hate' || phraseMetadata?.category === 'custom'
      ? phraseMetadata.category
      : categoryForOriginalWord(originalWord);
  return {
    offensive: true,
    category,
    matchedPatternId: String(match.termId),
  };
}

function passesAllowlist(normalized: string, compact: string): boolean {
  return isAllowlistedNormalizedName(normalized) || isAllowlistedNormalizedName(compact);
}

/** Strict check: substring matching on raw, normalized, and compact forms (admin scan/enforce). */
export function checkNameStrict(input: string): NameCheckResult {
  const trimmed = input.trim();
  if (!trimmed) {
    return { offensive: false };
  }

  const normalized = normalizeForModeration(trimmed);
  const compact = normalizeCompact(trimmed);

  if (passesAllowlist(normalized, compact)) {
    return { offensive: false };
  }

  const candidates = [trimmed, normalized, compact];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (!candidate || seen.has(candidate)) continue;
    seen.add(candidate);
    const match = matchOnCandidate(candidate);
    if (match) {
      return resultFromMatch(match);
    }
  }

  return { offensive: false };
}

function offensiveDefiniteMatch(candidate: string): NameCheckResult | null {
  const match = matchOnCandidate(candidate);
  if (!match) return null;
  if (matchCoversEntireCandidate(candidate, match)) {
    return resultFromMatch(match);
  }
  const result = resultFromMatch(match);
  if (result.category === 'hate' || result.category === 'custom') {
    return result;
  }
  return null;
}

/** Definite check: entire-token or entire-compact slur only (user pick / profile paths). */
export function checkNameDefinite(input: string): NameCheckResult {
  const trimmed = input.trim();
  if (!trimmed) {
    return { offensive: false };
  }

  const normalized = normalizeForModeration(trimmed);
  const compact = normalizeCompact(trimmed);

  if (passesAllowlist(normalized, compact)) {
    return { offensive: false };
  }

  if (compact) {
    const compactResult = offensiveDefiniteMatch(compact);
    if (compactResult) {
      return compactResult;
    }
  }

  const tokens = normalized.split(/\s+/).filter(Boolean);
  for (const token of tokens) {
    const tokenResult = offensiveDefiniteMatch(token);
    if (tokenResult) {
      return tokenResult;
    }
  }

  return { offensive: false };
}

/** @deprecated Use checkNameStrict or checkNameDefinite explicitly. */
export function checkName(input: string): NameCheckResult {
  return checkNameStrict(input);
}

export function isOffensiveNameStrict(input: string): boolean {
  return checkNameStrict(input).offensive;
}

export function isDefinitelyOffensiveName(input: string): boolean {
  return checkNameDefinite(input).offensive;
}

/** @deprecated Use isOffensiveNameStrict or isDefinitelyOffensiveName explicitly. */
export function isOffensiveName(input: string): boolean {
  return checkNameStrict(input).offensive;
}
